"use client";

import { useEffect, useRef } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          size?: "normal" | "compact" | "flexible";
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
        },
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

export type EngagementTurnstileProps = Readonly<{
  onTokenChange: (token: string | null) => void;
  size?: "normal" | "compact" | "flexible";
  onError?: () => void;
}>;

const turnstileScriptId = "cloudflare-turnstile-script";
const turnstileScriptSrc = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export function EngagementTurnstile({ onTokenChange, size = "normal", onError }: EngagementTurnstileProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY?.trim();

  useEffect(() => {
    if (!siteKey || !containerRef.current) {
      onTokenChange(null);
      return;
    }

    let cancelled = false;

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.turnstile) {
        return;
      }
      if (widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        size: size === "flexible" && containerRef.current.clientWidth < 300 ? "compact" : size,
        callback: (token) => { if (!cancelled) onTokenChange(token); },
        "expired-callback": () => { if (!cancelled) onTokenChange(null); },
        "error-callback": () => { if (!cancelled) { onTokenChange(null); onError?.(); } },
      });
    };

    const reportError = () => {
      if (cancelled) return;
      // A failed script never fires load again; a retry must create a fresh script.
      if (!window.turnstile) document.getElementById(turnstileScriptId)?.remove();
      onTokenChange(null);
      onError?.();
    };
    const existingScript = document.getElementById(turnstileScriptId) as HTMLScriptElement | null;
    if (existingScript) {
      if (window.turnstile) {
        renderWidget();
      } else {
        existingScript.addEventListener("load", renderWidget, { once: true });
        existingScript.addEventListener("error", reportError, { once: true });
      }
    } else {
      const script = document.createElement("script");
      script.id = turnstileScriptId;
      script.src = turnstileScriptSrc;
      script.async = true;
      script.defer = true;
      script.addEventListener("load", renderWidget, { once: true });
      script.addEventListener("error", reportError, { once: true });
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      const script = document.getElementById(turnstileScriptId);
      script?.removeEventListener("load", renderWidget);
      script?.removeEventListener("error", reportError);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
      onTokenChange(null);
    };
  }, [onTokenChange, siteKey, size, onError]);

  if (!siteKey) {
    return null;
  }

  return <div ref={containerRef} className="public-profile-engagement-modal__captcha" />;
}
